import api from './axiosInstance';
import { 
  Manufacturer, 
  MakeAndModel,
  CreateManufacturerData, 
  UpdateManufacturerData,
  CreateMakeAndModelData,
  UpdateMakeAndModelData,
  ManufacturerFilters,
  MakeAndModelFilters 
} from '../types/manufacturer';

// Manufacturer Service
export const manufacturerService = {
  // Get all manufacturers with filtering and pagination
  async getManufacturers(filters: ManufacturerFilters = {}) {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sortBy && filters.sortOrder) {
      params.append('sortBy', `${filters.sortBy}:${filters.sortOrder}`);
    }

    const response = await api.get(`/manufacturers?${params.toString()}`);
    return response.data;
  },

  // Get manufacturer by ID
  async getManufacturerById(id: string) {
    const response = await api.get(`/manufacturers/${id}`);
    return response.data;
  },

  // Create new manufacturer
  async createManufacturer(data: CreateManufacturerData) {
    const response = await api.post('/manufacturers', data);
    return response.data;
  },

  // Update manufacturer
  async updateManufacturer(id: string, data: UpdateManufacturerData) {
    const response = await api.put(`/manufacturers/${id}`, data);
    return response.data;
  },

  // Delete manufacturer
  async deleteManufacturer(id: string) {
    const response = await api.delete(`/manufacturers/${id}`);
    return response.data;
  },

  // Get manufacturers for dropdown (simplified)
  async getManufacturersForDropdown() {
    const response = await api.get('/manufacturers/dropdown');
    return response.data?.data || [];
  },

  // Bulk import manufacturers and their models
  async bulkImport(payload: {
    manufacturers: Array<{
      name: string;
      description?: string;
      website?: string;
      contactInfo?: { email?: string; phone?: string; address?: string };
      models?: Array<{
        name: string;
        description?: string;
        assetType?: string;
        specifications?: {
          capacity?: number;
          voltage?: number;
          power?: number;
          efficiency?: number;
        };
      }>;
    }>;
  }) {
    const response = await api.post('/manufacturers/bulk-import', payload);
    return response.data;
  },
};

// Make & Model Service
export const makeAndModelService = {
  // Get all make and models with filtering and pagination
  async getMakeAndModels(filters: MakeAndModelFilters = {}) {
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.manufacturerId) params.append('manufacturerId', filters.manufacturerId);
    if (filters.page) params.append('page', filters.page.toString());
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.sortBy && filters.sortOrder) {
      params.append('sortBy', `${filters.sortBy}:${filters.sortOrder}`);
    }

    const response = await api.get(`/make-and-models?${params.toString()}`);
    return response.data;
  },

  // Get make and models by manufacturer
  async getMakeAndModelsByManufacturer(manufacturerId: string) {
    const response = await api.get(`/make-and-models/by-manufacturer/${manufacturerId}`);
    return response.data;
  },

  // Get make and model by ID
  async getMakeAndModelById(id: string) {
    const response = await api.get(`/make-and-models/${id}`);
    return response.data;
  },

  // Create new make and model
  async createMakeAndModel(data: CreateMakeAndModelData) {
    const response = await api.post('/make-and-models', data);
    return response.data;
  },

  // Update make and model
  async updateMakeAndModel(id: string, data: UpdateMakeAndModelData) {
    const response = await api.put(`/make-and-models/${id}`, data);
    return response.data;
  },

  // Delete make and model
  async deleteMakeAndModel(id: string) {
    const response = await api.delete(`/make-and-models/${id}`);
    return response.data;
  },

  // Get make and models for dropdown (simplified)
  async getMakeAndModelsForDropdown() {
    const response = await api.get('/make-and-models/dropdown');
    return response.data?.data || [];
  }
};

export default { manufacturerService, makeAndModelService };